/**
 * Simple cache service to store and retrieve data
 * This helps reduce redundant API calls when navigating between pages
 */

// Cache expiration time in milliseconds (default: 5 minutes)
const DEFAULT_CACHE_EXPIRATION = 5 * 60 * 1000;

interface CacheItem<T> {
  data: T;
  timestamp: number;
  expiration: number;
}

export const cacheService = {
  /**
   * Set data in cache with a key
   * @param key Cache key
   * @param data Data to cache
   * @param expiration Optional expiration time in milliseconds
   */
  set: <T>(key: string, data: T, expiration = DEFAULT_CACHE_EXPIRATION): void => {
    const cacheItem: CacheItem<T> = {
      data,
      timestamp: Date.now(),
      expiration,
    };
    localStorage.setItem(`cache_${key}`, JSON.stringify(cacheItem));
  },

  /**
   * Get data from cache by key
   * @param key Cache key
   * @returns The cached data or null if not found or expired
   */
  get: <T>(key: string): T | null => {
    const cachedItem = localStorage.getItem(`cache_${key}`);
    
    if (!cachedItem) {
      return null;
    }

    try {
      const { data, timestamp, expiration } = JSON.parse(cachedItem) as CacheItem<T>;
      
      // Check if cache is expired
      if (Date.now() - timestamp > expiration) {
        localStorage.removeItem(`cache_${key}`);
        return null;
      }
      
      return data;
    } catch (error) {
      console.error('Error parsing cached data:', error);
      localStorage.removeItem(`cache_${key}`);
      return null;
    }
  },

  /**
   * Remove a specific item from cache
   * @param key Cache key
   */
  remove: (key: string): void => {
    localStorage.removeItem(`cache_${key}`);
  },

  /**
   * Clear all cached data
   */
  clear: (): void => {
    Object.keys(localStorage)
      .filter(key => key.startsWith('cache_'))
      .forEach(key => localStorage.removeItem(key));
  }
};